import { useEffect, useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import DeleteConfirmModal from '@/components/DeleteConfirmModal'
import { ArrowLeft, Send, Edit, Trash2, Mail, Video, CheckCircle, XCircle, Clock, Users, Loader2, AlertTriangle } from 'lucide-react'

interface Recipient {
  id: number
  agent_id: number
  agent_name: string
  agent_email: string
  status: string
  error_message: string | null
  sent_at: string | null
}

interface Message {
  id: number
  subject: string
  body: string
  video_url: string | null
  status: string
  sender_email: string
  sender_name: string
  total_recipients: number
  sent_count: number
  failed_count: number
  pending_count: number
  unconfirmed_count: number
  in_flight: boolean
  sent_at: string | null
  created_at: string
  updated_at: string
  created_by_name: string
  target_agent_name?: string | null
  recipients?: Recipient[]
}

interface Props extends PageProps {
  message: Message
  recipients: Recipient[]
}

export default function MessageShow({ message, recipients }: Props) {
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showSendConfirm, setShowSendConfirm] = useState(false)
  const [sending, setSending] = useState(false)

  const canEdit = message.status === 'draft' || message.status === 'queued'
  const canSend = message.status === 'draft' || message.status === 'queued'
  const canDelete = message.status === 'draft' || message.status === 'queued'

  const statusColors: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-700',
    queued: 'bg-yellow-100 text-yellow-700',
    sending: 'bg-blue-100 text-blue-700',
    sent: 'bg-green-100 text-green-700',
    partially_sent: 'bg-amber-100 text-amber-700',
    failed: 'bg-red-100 text-red-700'
  }

  const statusLabels: Record<string, string> = {
    draft: 'Draft',
    queued: 'Queued',
    sending: 'Sending',
    sent: 'Sent',
    partially_sent: 'Partially sent',
    failed: 'Failed'
  }

  const isBlast = !message.target_agent_name
  const progressTotal = message.total_recipients || 0
  const processed = message.sent_count + message.failed_count
  const progressPercent = progressTotal > 0 ? Math.round((processed / progressTotal) * 100) : 0
  const showOutcome = ['sent', 'partially_sent', 'failed', 'sending'].includes(message.status) && progressTotal > 0

  // The blast runs in a background job — poll while it is still working so the
  // counters climb without the admin refreshing.
  useEffect(() => {
    if (!message.in_flight) return
    const timer = setInterval(() => {
      router.reload({ only: ['message', 'recipients'] })
    }, 4000)
    return () => clearInterval(timer)
  }, [message.in_flight])

  const recipientStatusColors: Record<string, string> = {
    pending: 'bg-gray-100 text-gray-700',
    sending: 'bg-blue-100 text-blue-700',
    sent: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
    unconfirmed: 'bg-amber-100 text-amber-700'
  }

  const recipientStatusLabels: Record<string, string> = {
    pending: 'waiting',
    sending: 'sending',
    sent: 'sent',
    failed: 'failed',
    unconfirmed: 'unconfirmed'
  }

  const handleSend = () => {
    setSending(true)
    router.post(`/admin/messages/${message.id}/send_message`, {}, {
      onFinish: () => {
        setSending(false)
        setShowSendConfirm(false)
      }
    })
  }

  return (
    <AppLayout>
      <Head title={`Message: ${message.subject}`} />

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/admin/messages" className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Message Details</h1>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-2 py-1 text-xs font-medium rounded-full inline-flex items-center gap-1 ${statusColors[message.status]}`}>
                  {message.in_flight && <Loader2 className="h-3 w-3 animate-spin" />}
                  {statusLabels[message.status] ?? message.status}
                </span>
                <span className="text-chestnut-charcoal/50 text-sm">Created {new Date(message.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canSend && (
              <button
                onClick={() => setShowSendConfirm(true)}
                className="btn-primary flex items-center gap-2"
              >
                <Send className="h-4 w-4" />
                Send Now
              </button>
            )}
            {canEdit && (
              <Link href={`/admin/messages/${message.id}/edit`} className="btn-secondary flex items-center gap-2">
                <Edit className="h-4 w-4" />
                Edit
              </Link>
            )}
            {canDelete && (
              <button onClick={() => setShowDeleteModal(true)} className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg">
                <Trash2 className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        {showOutcome && (
          <div className="space-y-4">
            {message.in_flight && (
              <div className="card p-4 space-y-2 border-l-4 border-l-blue-500">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                  <p className="text-sm font-medium text-chestnut-charcoal">
                    {isBlast ? 'Sending in the background' : 'Sending'} — {processed} of {progressTotal} processed
                    {message.pending_count > 0 && `, ${message.pending_count} to go`}
                  </p>
                </div>
                <div className="h-2 w-full bg-chestnut-charcoal/10 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 transition-all" style={{ width: `${progressPercent}%` }} />
                </div>
                <p className="text-xs text-chestnut-charcoal/60">This page refreshes automatically until the send finishes.</p>
              </div>
            )}

            {message.status === 'failed' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
                This message reached none of its {message.total_recipients} recipient(s). See the per-recipient errors below,
                or the <Link href="/admin/email_activity?status=failed" className="underline">Email Activity</Link> log for the provider's response.
              </div>
            )}

            {message.unconfirmed_count > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800 flex gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  {message.unconfirmed_count} recipient(s) were mid-send when the server stopped, so the outcome is
                  unknown. They were deliberately not resent — that could email them twice. Check the{' '}
                  <Link href="/admin/email_activity" className="underline">Email Activity</Link> log to see whether the
                  attempt reached the provider.
                </span>
              </div>
            )}

            {message.status === 'partially_sent' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                Partially sent — {message.sent_count} of {message.total_recipients} recipient(s) received this message and{' '}
                {message.failed_count} failed.
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="card border-l-4 border-l-blue-500">
                <h3 className="text-sm font-medium text-chestnut-charcoal/60 uppercase tracking-wide">Total Recipients</h3>
                <p className="text-2xl font-bold text-chestnut-charcoal mt-1">{message.total_recipients}</p>
              </div>
              <div className="card border-l-4 border-l-green-500">
                <h3 className="text-sm font-medium text-chestnut-charcoal/60 uppercase tracking-wide">Delivered</h3>
                <p className="text-2xl font-bold text-green-600 mt-1">{message.sent_count}</p>
              </div>
              <div className="card border-l-4 border-l-red-500">
                <h3 className="text-sm font-medium text-chestnut-charcoal/60 uppercase tracking-wide">Failed</h3>
                <p className="text-2xl font-bold text-red-600 mt-1">{message.failed_count}</p>
              </div>
              <div className="card border-l-4 border-l-gray-400">
                <h3 className="text-sm font-medium text-chestnut-charcoal/60 uppercase tracking-wide">In Progress</h3>
                <p className="text-2xl font-bold text-chestnut-charcoal mt-1">{message.pending_count}</p>
              </div>
            </div>
          </div>
        )}

        <div className="card p-6 space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b border-chestnut-charcoal/10">
            <div className="p-3 bg-chestnut-forest/10 rounded-lg">
              <Mail className="h-6 w-6 text-chestnut-forest" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-chestnut-charcoal">{message.subject}</h2>
              <p className="text-sm text-chestnut-charcoal/60">
                From: {message.sender_name} &lt;{message.sender_email}&gt;
              </p>
            </div>
          </div>

          {message.video_url && (
            <div className="flex items-center gap-3 p-3 bg-chestnut-forest/5 rounded-lg">
              <Video className="h-5 w-5 text-chestnut-forest" />
              <div>
                <p className="text-sm font-medium text-chestnut-charcoal">Video Attachment</p>
                <a href={message.video_url} target="_blank" rel="noopener noreferrer" className="text-sm text-chestnut-forest hover:underline">
                  {message.video_url}
                </a>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-2">Message Body</label>
            <div
              className="prose prose-sm max-w-none p-4 bg-white border border-chestnut-charcoal/10 rounded-lg"
              dangerouslySetInnerHTML={{ __html: message.body }}
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-chestnut-charcoal/10 text-sm">
            <div>
              <p className="text-chestnut-charcoal/60">Created By</p>
              <p className="font-medium text-chestnut-charcoal">{message.created_by_name}</p>
            </div>
            <div>
              <p className="text-chestnut-charcoal/60">Created</p>
              <p className="font-medium text-chestnut-charcoal">{new Date(message.created_at).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-chestnut-charcoal/60">Last Updated</p>
              <p className="font-medium text-chestnut-charcoal">{new Date(message.updated_at).toLocaleDateString()}</p>
            </div>
            {message.sent_at && (
              <div>
                <p className="text-chestnut-charcoal/60">Sent At</p>
                <p className="font-medium text-chestnut-charcoal">{new Date(message.sent_at).toLocaleString()}</p>
              </div>
            )}
          </div>
        </div>

        {recipients.length > 0 && (
          <div className="card p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-chestnut-forest" />
              <h3 className="text-lg font-semibold text-chestnut-charcoal">Recipients ({recipients.length})</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-chestnut-charcoal/10">
                  <tr className="text-left text-sm text-chestnut-charcoal/60">
                    <th className="pb-3 font-medium">Agent</th>
                    <th className="pb-3 font-medium">Email</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium">Sent At</th>
                    <th className="pb-3 font-medium">Error</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-chestnut-charcoal/5">
                  {recipients.map((r) => (
                    <tr key={r.id}>
                      <td className="py-3 font-medium text-chestnut-charcoal">{r.agent_name}</td>
                      <td className="py-3 text-sm text-chestnut-charcoal/70">{r.agent_email}</td>
                      <td className="py-3">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${recipientStatusColors[r.status]}`}>
                          {r.status === 'sent' && <CheckCircle className="h-3 w-3 inline mr-1" />}
                          {r.status === 'failed' && <XCircle className="h-3 w-3 inline mr-1" />}
                          {r.status === 'unconfirmed' && <AlertTriangle className="h-3 w-3 inline mr-1" />}
                          {(r.status === 'pending' || r.status === 'sending') && <Clock className="h-3 w-3 inline mr-1" />}
                          {recipientStatusLabels[r.status] ?? r.status}
                        </span>
                      </td>
                      <td className="py-3 text-sm text-chestnut-charcoal/70">
                        {r.sent_at ? new Date(r.sent_at).toLocaleString() : '-'}
                      </td>
                      <td className="py-3 text-sm text-red-600">{r.error_message || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {message.status === 'queued' && message.recipients && message.recipients.length > 0 && recipients.length === 0 && (
          <div className="card p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-chestnut-forest" />
              <h3 className="text-lg font-semibold text-chestnut-charcoal">Suggested Recipients</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-chestnut-charcoal/10">
                  <tr className="text-left text-sm text-chestnut-charcoal/60">
                    <th className="pb-3 font-medium">Agent</th>
                    <th className="pb-3 font-medium">Email</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-chestnut-charcoal/5">
                  {message.recipients.map((r) => (
                    <tr key={r.id}>
                      <td className="py-3 font-medium text-chestnut-charcoal">{r.agent_name}</td>
                      <td className="py-3 text-sm text-chestnut-charcoal/70">{r.agent_email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Message"
        message="Are you sure you want to delete this message? This action cannot be undone."
        deleteUrl={`/admin/messages/${message.id}`}
        itemName={message.subject}
        redirectUrl="/admin/messages"
      />

      {showSendConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => !sending && setShowSendConfirm(false)} />
          <div className="relative bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-chestnut-forest/10 rounded-lg">
                <Send className="h-5 w-5 text-chestnut-forest" />
              </div>
              <h3 className="text-lg font-semibold text-chestnut-charcoal">Send Message</h3>
            </div>
            <p className="text-sm text-chestnut-charcoal/70 mb-2">
              {isBlast ? (
                <>This will send the email "<strong>{message.subject}</strong>" to all active agents in the system.</>
              ) : (
                <>This will send the email "<strong>{message.subject}</strong>" to <strong>{message.target_agent_name}</strong>.</>
              )}
            </p>
            <p className="text-sm text-chestnut-charcoal/70 mb-2">
              The email will be sent from <strong>{message.sender_name}</strong> ({message.sender_email}).
            </p>
            <p className="text-sm text-chestnut-charcoal/70 mb-6">
              {isBlast
                ? 'Sending happens in the background — you can leave this page and come back; progress updates here as it runs.'
                : 'This send happens right away and reports success or failure immediately.'}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowSendConfirm(false)}
                disabled={sending}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={sending}
                className="btn-primary flex items-center gap-2"
              >
                <Send className="h-4 w-4" />
                {sending ? 'Sending...' : isBlast ? 'Send to All Agents' : 'Send Now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
